import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Trash2, MousePointer2, ImagePlus, FileText, Table, StickyNote, ListChecks, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import NoteCard from './NoteCard';
import StickerItem from './StickerItem';
import ImageItem from './ImageItem';
import TableItem from './TableItem';
import TodoItem from './TodoItem';
import SourceItem from './SourceItem';
import DrawingCanvas from './DrawingCanvas';
import NoteSticker from './NoteSticker';
import { toast } from 'sonner';
import { uploadImageToServer, fetchImageFromUrl, extractDropUrl, extractClipboardImage, resolveImageUrl, getImageNaturalSize, computeDropSize } from '../lib/imageUtils';

export const Canvas = ({ 
  notes, 
  totalNoteCount,
  stickers,
  noteStickers = [],
  images = [],
  tables = [],
  todos = [],
  sources = [],
  drawings,
  setDrawings,
  drawingTool,
  updateSource,
  deleteSource,
  addNote,
  updateNote, 
  deleteNote,
  addSticker,
  updateSticker,
  deleteSticker,
  addImage,
  updateImage,
  deleteImage,
  addTable,
  updateTable,
  deleteTable,
  addTodo,
  updateTodo,
  deleteTodo,
  onAddSource,
  addNoteSticker,
  folders,
  isPremium,
  isDrawingMode,
  onCloseDrawing,
  userName,
  activeFolder,
  // NoteSticker-specific handlers
  updateNoteSticker,
  deleteNoteSticker,
  // Opens the trash modal in parent
  onOpenTrash,
  onNoteClick,
  onOpenNoteTools,
  readerModeNoteId,
  onReaderModeClosed,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const trashRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1600, height: 1000 });
  const [zoom, setZoom] = useState(1);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { screenX, screenY, canvasX, canvasY }

  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]); // Array of { type, id }
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null); // { startX, startY, currentX, currentY }
  const [selectMode, setSelectMode] = useState(false); // Toggle select mode on/off

  // Touch pinch-zoom state (for iPad/phones)
  const pinchState = useRef({
    isPinching: false,
    startDistance: 0,
    startZoom: 1,
  });

  // Mirrors `zoom` for use inside the wheel handler. A rapid wheel gesture fires
  // many events faster than React can re-render and hand handleZoom a fresh
  // `zoom` closure, so reading/writing this ref (instead of depending on the
  // `zoom` state) keeps each tick anchored to the truly-current value.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // Drawing persistence is now managed in MainApp via the shared `drawings` prop.

  // Calculate canvas size based on content and grow as needed (right & bottom only)
  useEffect(() => {
    const padding = 300;
    const minWidth = 1600;
    const minHeight = 1000;

    let maxX = 0;
    let maxY = 0;

    const consider = (item, defaultWidth, defaultHeight) => {
      if (!item || !item.position) return;
      const width = item.size?.width || defaultWidth;
      const height = item.size?.height || defaultHeight;
      maxX = Math.max(maxX, item.position.x + width);
      maxY = Math.max(maxY, item.position.y + height);
    };

    notes.forEach(note => consider(note, 320, 200));
    stickers.forEach(sticker => consider(sticker, 100, 100));
    noteStickers.forEach(sticker => consider(sticker, 200, 160));
    images.forEach(image => consider(image, 300, 200));
    tables.forEach(table => consider(table, 400, 200));
    todos.forEach(todo => consider(todo, 280, 200));
    sources.forEach(source => consider(source, 260, 120));

    const targetWidth = Math.max(minWidth, maxX + padding);
    const targetHeight = Math.max(minHeight, maxY + padding);

    setCanvasSize(prev => ({
      width: Math.max(prev.width, targetWidth),
      height: Math.max(prev.height, targetHeight),
    }));
  }, [notes, stickers, noteStickers, images, tables, todos, sources]);

  // Zoom handlers — anchored so the point under (clientX, clientY) stays put,
  // same math as the pinch-to-zoom handler below. Falls back to the viewport
  // center when no cursor position is given (e.g. the +/- toolbar buttons).
  const handleZoom = useCallback((delta, clientX, clientY) => {
    const container = containerRef.current;
    const prevZoom = zoomRef.current;
    const newZoom = Math.min(2.5, Math.max(0.15, prevZoom + delta));
    if (newZoom === prevZoom) return;

    if (!container) {
      zoomRef.current = newZoom;
      setZoom(newZoom);
      return;
    }

    const rect = container.getBoundingClientRect();
    const anchorX = clientX ?? rect.left + rect.width / 2;
    const anchorY = clientY ?? rect.top + rect.height / 2;

    const offsetX = anchorX - rect.left + container.scrollLeft;
    const offsetY = anchorY - rect.top + container.scrollTop;
    const contentX = offsetX / prevZoom;
    const contentY = offsetY / prevZoom;
    const newScrollLeft = contentX * newZoom - (anchorX - rect.left);
    const newScrollTop = contentY * newZoom - (anchorY - rect.top);

    zoomRef.current = newZoom;
    setZoom(newZoom);
    container.scrollLeft = newScrollLeft;
    container.scrollTop = newScrollTop;
  }, []);

  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      // Scale with how far the wheel actually moved instead of a fixed step per
      // event, so a trackpad's stream of small continuous deltas zooms smoothly
      // instead of jumping by the same coarse increment on every tick. Clamped
      // so a single mouse-wheel notch (a much bigger deltaY) can't overshoot.
      const rawDelta = -e.deltaY * 0.002;
      const delta = Math.max(-0.04, Math.min(0.04, rawDelta));
      handleZoom(delta, e.clientX, e.clientY);
    }
  }, [handleZoom]);

  // Touch pinch-to-zoom (mobile/tablet), anchored around finger center
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getDistance = (touch1, touch2) => {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        const dist = getDistance(e.touches[0], e.touches[1]);
        pinchState.current = {
          isPinching: true,
          startDistance: dist,
          startZoom: zoom,
        };
      }
    };

    const handleTouchMove = (e) => {
      if (!pinchState.current.isPinching || e.touches.length < 2) return;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = getDistance(touch1, touch2);
      if (pinchState.current.startDistance === 0) return;

      // Prevent browser/page zoom while pinching inside the canvas
      e.preventDefault();

      const oldZoom = pinchState.current.startZoom || zoom;
      const scaleDelta = dist / pinchState.current.startDistance;
      const nextZoomRaw = oldZoom * scaleDelta;
      const nextZoom = Math.min(2.5, Math.max(0.15, nextZoomRaw));

      const rect = container.getBoundingClientRect();
      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;

      // Position of pinch center in scrolled content BEFORE zoom
      const offsetX = centerX - rect.left + container.scrollLeft;
      const offsetY = centerY - rect.top + container.scrollTop;
      const contentX = offsetX / oldZoom;
      const contentY = offsetY / oldZoom;

      // After zoom, keep the same content point under the pinch center
      const newScrollLeft = contentX * nextZoom - (centerX - rect.left);
      const newScrollTop = contentY * nextZoom - (centerY - rect.top);

      setZoom(nextZoom);
      container.scrollLeft = newScrollLeft;
      container.scrollTop = newScrollTop;

      // Prepare for next incremental step
      pinchState.current.startZoom = nextZoom;
      pinchState.current.startDistance = dist;
    };

    const handleTouchEnd = () => {
      pinchState.current = {
        isPinching: false,
        startDistance: 0,
        startZoom: zoom,
      };
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    // touchmove must be non-passive so we can call preventDefault during pinch
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [zoom]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Multi-select box logic
  const getCanvasCoordinates = useCallback((clientX, clientY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    // rect already accounts for scroll position, just need to account for zoom
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom
    };
  }, [zoom]);

  const handleCanvasMouseDown = useCallback((e) => {
    // Start a drag-select box on a plain left-click-drag on empty canvas —
    // no need to toggle select mode first. Only the primary mouse button
    // triggers this, so panning/context-click/etc. are unaffected.
    if (e.button !== 0) return;
    if (drawingTool) return; // Don't select while drawing

    // Check if clicking on an item - if so, don't start selection box
    const target = e.target;
    if (target !== canvasRef.current && !target.hasAttribute('data-anotequest-canvas-board')) {
      return;
    }

    // Prevent default to stop text selection
    e.preventDefault();

    const coords = getCanvasCoordinates(e.clientX, e.clientY);
    setIsSelecting(true);
    setSelectionBox({
      startX: coords.x,
      startY: coords.y,
      currentX: coords.x,
      currentY: coords.y
    });
    // Clear selection when starting a new box
    setSelectedItems([]);
  }, [drawingTool, getCanvasCoordinates]);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!isSelecting || !selectionBox) return;
    
    // Prevent text selection while dragging
    e.preventDefault();
    
    const coords = getCanvasCoordinates(e.clientX, e.clientY);
    setSelectionBox(prev => ({
      ...prev,
      currentX: coords.x,
      currentY: coords.y
    }));
  }, [isSelecting, selectionBox, getCanvasCoordinates]);

  const handleCanvasMouseUp = useCallback(() => {
    if (!isSelecting || !selectionBox) {
      setIsSelecting(false);
      setSelectionBox(null);
      return;
    }

    // Calculate selection box bounds
    const minX = Math.min(selectionBox.startX, selectionBox.currentX);
    const maxX = Math.max(selectionBox.startX, selectionBox.currentX);
    const minY = Math.min(selectionBox.startY, selectionBox.currentY);
    const maxY = Math.max(selectionBox.startY, selectionBox.currentY);

    // Check if box is too small (just a click)
    if (maxX - minX < 10 && maxY - minY < 10) {
      setIsSelecting(false);
      setSelectionBox(null);
      return;
    }

    // Find all items that intersect with the selection box
    const newSelectedItems = [];

    const intersects = (item, defaultWidth, defaultHeight) => {
      if (!item?.position) return false;
      const itemX = item.position.x;
      const itemY = item.position.y;
      const itemWidth = item.size?.width || defaultWidth;
      const itemHeight = item.size?.height || defaultHeight;
      
      // Check if rectangles overlap
      return !(itemX + itemWidth < minX || 
               itemX > maxX || 
               itemY + itemHeight < minY || 
               itemY > maxY);
    };

    notes.forEach(note => {
      if (intersects(note, 320, 200)) {
        newSelectedItems.push({ type: 'note', id: note.id });
      }
    });

    stickers.forEach(sticker => {
      if (intersects(sticker, 60, 60)) {
        newSelectedItems.push({ type: 'sticker', id: sticker.id });
      }
    });

    noteStickers.forEach(sticker => {
      if (intersects(sticker, 200, 160)) {
        newSelectedItems.push({ type: 'noteSticker', id: sticker.id });
      }
    });

    images.forEach(image => {
      if (intersects(image, 300, 200)) {
        newSelectedItems.push({ type: 'image', id: image.id });
      }
    });

    tables.forEach(table => {
      if (intersects(table, 400, 200)) {
        newSelectedItems.push({ type: 'table', id: table.id });
      }
    });

    todos.forEach(todo => {
      if (intersects(todo, 280, 200)) {
        newSelectedItems.push({ type: 'todo', id: todo.id });
      }
    });

    sources.forEach(source => {
      if (intersects(source, 260, 120)) {
        newSelectedItems.push({ type: 'source', id: source.id });
      }
    });

    setSelectedItems(newSelectedItems);
    if (newSelectedItems.length > 0) {
      toast.success(`Selected ${newSelectedItems.length} item${newSelectedItems.length > 1 ? 's' : ''}`);
    }

    setIsSelecting(false);
    setSelectionBox(null);
  }, [isSelecting, selectionBox, notes, stickers, noteStickers, images, tables, todos, sources]);

  // Handle moving all selected items together
  const handleMultiDrag = useCallback((deltaX, deltaY, draggedType, draggedId) => {
    if (selectedItems.length <= 1) return;
    
    // Move all selected items except the one being dragged (it moves itself)
    selectedItems.forEach(({ type, id }) => {
      if (type === draggedType && id === draggedId) return; // Skip the dragged item
      
      switch (type) {
        case 'note': {
          const note = notes.find(n => n.id === id);
          if (note) {
            updateNote(id, {
              position: {
                x: note.position.x + deltaX,
                y: note.position.y + deltaY
              }
            });
          }
          break;
        }
        case 'sticker': {
          const sticker = stickers.find(s => s.id === id);
          if (sticker) {
            updateSticker(id, {
              position: {
                x: sticker.position.x + deltaX,
                y: sticker.position.y + deltaY
              }
            });
          }
          break;
        }
        case 'noteSticker': {
          const noteSticker = noteStickers.find(s => s.id === id);
          if (noteSticker) {
            updateNoteSticker(id, {
              position: {
                x: noteSticker.position.x + deltaX,
                y: noteSticker.position.y + deltaY
              }
            });
          }
          break;
        }
        case 'image': {
          const image = images.find(i => i.id === id);
          if (image) {
            updateImage(id, {
              position: {
                x: image.position.x + deltaX,
                y: image.position.y + deltaY
              }
            });
          }
          break;
        }
        case 'table': {
          const table = tables.find(t => t.id === id);
          if (table) {
            updateTable(id, {
              position: {
                x: table.position.x + deltaX,
                y: table.position.y + deltaY
              }
            });
          }
          break;
        }
        case 'todo': {
          const todo = todos.find(t => t.id === id);
          if (todo) {
            updateTodo(id, {
              position: {
                x: todo.position.x + deltaX,
                y: todo.position.y + deltaY
              }
            });
          }
          break;
        }
        case 'source': {
          const source = sources.find(s => s.id === id);
          if (source) {
            updateSource(id, {
              position: {
                x: source.position.x + deltaX,
                y: source.position.y + deltaY
              }
            });
          }
          break;
        }
        default:
          break;
      }
    });
  }, [selectedItems, notes, stickers, noteStickers, images, tables, todos, sources, 
      updateNote, updateSticker, updateNoteSticker, updateImage, updateTable, updateTodo, updateSource]);

  // Clear selection when clicking outside or pressing Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedItems([]);
        setSelectMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Shared helper — upload a file blob or fetch a remote URL, then place on canvas
  const handleImageUpload = useCallback(async (fileOrUrl, position = { x: 200, y: 200 }) => {
    try {
      toast.loading('Processing image...');
      const imageData = typeof fileOrUrl === 'string'
        ? await fetchImageFromUrl(fileOrUrl)
        : await uploadImageToServer(fileOrUrl);
      if (addImage) {
        const resolvedUrl = await resolveImageUrl(imageData);
        const naturalSize = await getImageNaturalSize(resolvedUrl);
        const size = computeDropSize(naturalSize?.width, naturalSize?.height);
        addImage({
          type: 'image',
          data: imageData,
          position: { x: position.x - size.width / 2, y: position.y - size.height / 2 },
          size,
        });
      }
      toast.dismiss();
      toast.success('Image added!');
    } catch (err) {
      toast.dismiss();
      toast.error(err?.message || 'Failed to process image');
    }
  }, [addImage]);

  // Ctrl+V / Cmd+V paste — handles both image blobs and URL strings
  useEffect(() => {
    const handlePaste = async (e) => {
      const result = extractClipboardImage(e.clipboardData);
      if (!result) return;

      if (result.blob) {
        await handleImageUpload(result.blob);
        return;
      }
      if (result.urlPromise) {
        const url = (await result.urlPromise)?.trim();
        if (url?.startsWith('http')) await handleImageUpload(url);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handleImageUpload]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const addNoteAtPosition = useCallback((canvasX, canvasY) => {
    closeContextMenu();
    if (!addNote) return;
    addNote({ position: { x: canvasX - 160, y: canvasY - 100 } });
    toast.success('Note added!');
  }, [addNote, closeContextMenu]);

  const addTableAtPosition = useCallback((canvasX, canvasY) => {
    closeContextMenu();
    if (!addTable) return;
    addTable({
      type: 'table',
      position: { x: canvasX - 200, y: canvasY - 100 },
      size: { width: 400, height: 200 },
      rows: 3,
      cols: 3,
      data: [['', '', ''], ['', '', ''], ['', '', '']],
    });
    toast.success('Table added!');
  }, [addTable, closeContextMenu]);

  const addNoteStickerAtPosition = useCallback((canvasX, canvasY) => {
    closeContextMenu();
    if (!addNoteSticker) return;
    addNoteSticker({ position: { x: canvasX - 100, y: canvasY - 80 } });
    toast.success('Note sticker added!');
  }, [addNoteSticker, closeContextMenu]);

  const addTodoAtPosition = useCallback((canvasX, canvasY) => {
    closeContextMenu();
    if (!addTodo) return;
    addTodo({
      type: 'todo',
      position: { x: canvasX - 140, y: canvasY - 100 },
      size: { width: 280, height: 200 },
      title: 'Todo list',
      items: [{ id: Date.now(), text: '', completed: false }],
    });
    toast.success('Todo list added!');
  }, [addTodo, closeContextMenu]);

  const pasteUrlAtPosition = useCallback(async (canvasX, canvasY) => {
    closeContextMenu();
    const rawUrl = window.prompt('Paste a URL to add to the canvas:');
    if (!rawUrl) return;
    let url = rawUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    try {
      const imageLike = /\.(jpe?g|png|gif|webp|avif|svg|bmp)(\?.*)?$/i.test(url) || /pinimg\.com|googleusercontent\.com|unsplash\.com|imgur\.com/i.test(url);
      if (imageLike) {
        await handleImageUpload(url, { x: canvasX, y: canvasY });
        return;
      }
      if (!onAddSource) {
        toast.error('Link support not available');
        return;
      }
      onAddSource({
        url,
        position: { x: canvasX - 130, y: canvasY - 60 },
        size: { width: 260, height: 120 },
      });
      toast.success('URL added. Preview will load shortly.');
    } catch (err) {
      toast.error(err?.message || 'Could not add URL');
    }
  }, [closeContextMenu, handleImageUpload, onAddSource]);

  // Right-click context menu — paste image at cursor position
  const handleContextMenu = useCallback((e) => {
    // Only intercept right-clicks directly on the canvas background
    if (e.target !== containerRef.current && !e.target.hasAttribute('data-anotequest-canvas-board')) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const scrollLeft = containerRef.current.scrollLeft;
    const scrollTop = containerRef.current.scrollTop;
    setContextMenu({
      screenX: e.clientX,
      screenY: e.clientY,
      canvasX: (e.clientX - rect.left + scrollLeft) / zoom,
      canvasY: (e.clientY - rect.top + scrollTop) / zoom,
    });
  }, [zoom]);

  const pasteImageAtPosition = useCallback(async (canvasX, canvasY) => {
    closeContextMenu();
    const pos = { x: canvasX, y: canvasY };
    try {
      const clipItems = await navigator.clipboard.read();
      for (const item of clipItems) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          await handleImageUpload(blob, pos);
          return;
        }
        if (item.types.includes('text/plain')) {
          const url = (await (await item.getType('text/plain')).text())?.trim();
          if (url?.startsWith('http')) {
            await handleImageUpload(url, pos);
            return;
          }
        }
      }
      toast.error('No image found in clipboard');
    } catch {
      toast.error('Could not read clipboard — try Ctrl+V instead');
    }
  }, [handleImageUpload, closeContextMenu]);

  // Check if an item is selected
  const isItemSelected = useCallback((type, id) => {
    return selectedItems.some(item => item.type === type && item.id === id);
  }, [selectedItems]);

  // Drag and drop handling (images + internal items)
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';

    const types = Array.from(e.dataTransfer?.types || []);
    const isImageDrag = types.includes('Files') || types.includes('text/uri-list') || types.includes('text/html');
    setIsDraggingOver(isImageDrag);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    // Get drop position relative to canvas
    const rect = canvasRef.current.getBoundingClientRect();
    const scrollLeft = containerRef.current.scrollLeft;
    const scrollTop = containerRef.current.scrollTop;
    const x = (e.clientX - rect.left + scrollLeft) / zoom;
    const y = (e.clientY - rect.top + scrollTop) / zoom;

    // First, handle internal drag-from-toolbar items
    const itemData = e.dataTransfer.getData('application/anotequest-item');
    if (itemData) {
      try {
        const item = JSON.parse(itemData);
        const basePosition = { x, y };

        switch (item.kind) {
          case 'note':
            if (addNote) {
              addNote({ position: { x: basePosition.x - 160, y: basePosition.y - 120 } });
              toast.success('Note created!');
            }
            return;
          case 'noteSticker':
            // creation of note stickers happens via parent handler; here we just place a basic one via addNote
            return;
          case 'table':
            if (addTable) {
              addTable({
                type: 'table',
                position: { x: basePosition.x - 200, y: basePosition.y - 100 },
                size: { width: 400, height: 200 },
                rows: 3,
                cols: 3,
                data: [['', '', ''], ['', '', ''], ['', '', '']],
              });
              toast.success('Table added!');
            }
            return;
          case 'todo':
            if (addTodo) {
              addTodo({
                type: 'todo',
                position: { x: basePosition.x - 140, y: basePosition.y - 100 },
                size: { width: 280, height: 200 },
                title: 'Todo List',
                items: [{ id: Date.now(), text: '', completed: false }],
              });
              toast.success('Todo list added!');
            }
            return;
          case 'sticker':
            if (addSticker) {
              addSticker({
                type: item.type,
                position: { x: basePosition.x - 30, y: basePosition.y - 30 },
                size: { width: 60, height: 60 },
                rotation: 0,
              });
              toast.success('Sticker added!');
            }
            return;
          default:
            break;
        }
      } catch (err) {
        console.error('Failed to parse internal drag data', err);
      }
    }

    // 1. Direct file drop (local file from desktop / file manager)
    const imageFile = Array.from(e.dataTransfer.files)
      .find(f => f.type.startsWith('image/'));
    if (imageFile) {
      await handleImageUpload(imageFile, { x, y });
      return;
    }

    // 2–3. Remote URL from uri-list / text/html / text/plain
    // (Pinterest clears dataTransfer entirely so this won't help there,
    //  but it works for Google Images, Wikipedia, Unsplash, etc.)
    const remoteUrl = extractDropUrl(e.dataTransfer);
    if (remoteUrl) {
      await handleImageUpload(remoteUrl, { x, y });
      return;
    }

    // 4. Nothing usable — guide the user to the clipboard fallback
    toast.error('Right-click the image → Copy Image, then right-click canvas → Paste image');
  };

  // Connection feature removed

  const getItemPosition = (itemId, itemType) => {
    let item;
    switch (itemType) {
      case 'note':
        item = notes.find(n => n.id === itemId);
        if (item) {
          return {
            x: item.position.x + (item.size?.width || 320) / 2,
            y: item.position.y + (item.size?.height || 200) / 2
          };
        }
        break;
      case 'image':
        item = images.find(i => i.id === itemId);
        if (item) {
          return {
            x: item.position.x + (item.size?.width || 300) / 2,
            y: item.position.y + (item.size?.height || 200) / 2
          };
        }
        break;
      default:
        return null;
    }
    return null;
  };

  const shouldDeleteOnDrop = useCallback((e) => {
    if (!trashRef.current || !e) return false;
    const rect = trashRef.current.getBoundingClientRect();

    let clientX;
    let clientY;

    // Support both mouse and touch end events
    if (typeof TouchEvent !== 'undefined' && e instanceof TouchEvent) {
      const touch = e.changedTouches && e.changedTouches[0];
      if (!touch) return false;
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else if ('changedTouches' in e && e.changedTouches && e.changedTouches[0]) {
      // Fallback for generic events from libraries
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    if (clientX == null || clientY == null) return false;

    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }, []);

  return (
    <div className="relative w-full h-full">
 {/* Zoom Controls – melted into header */}
<div
  className="
    fixed
    top-12
    left-1/2
    -translate-x-1/2
    z-40

    flex items-center gap-1
    px-3 py-0.5

    bg-card/95 backdrop-blur-md
    border border-border border-t-0

    rounded-b-xl
    shadow-[0_8px_20px_-10px_rgba(0,0,0,0.35)]
  "
>
  <Button
    variant="ghost"
    size="icon"
    className="h-6 w-6"
    onClick={() => handleZoom(-0.1)}
    disabled={zoom <= 0.15}
  >
    <ZoomOut className="h-3.5 w-3.5" />
  </Button>

  <span className="text-[11px] font-medium w-10 text-center">
    {Math.round(zoom * 100)}%
  </span>

  <Button
    variant="ghost"
    size="icon"
    className="h-6 w-6"
    onClick={() => handleZoom(0.1)}
    disabled={zoom >= 2.5}
  >
    <ZoomIn className="h-3.5 w-3.5" />
  </Button>

  <Button
    variant="ghost"
    size="icon"
    className="h-6 w-6"
    onClick={() => setZoom(1)}
    disabled={zoom === 1}
  >
    <RotateCcw className="h-3 w-3" />
  </Button>

  <div className="w-px h-4 bg-border mx-1" />

  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={selectMode ? "default" : "ghost"}
          size="icon"
          className="h-6 w-6"
          onClick={() => {
            setSelectMode(!selectMode);
            if (selectMode) {
              setSelectedItems([]);
            }
          }}
        >
          <MousePointer2 className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{selectMode ? 'Exit select mode (Esc)' : 'Pin select cursor (or just click-drag on the canvas)'}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
  
  {selectedItems.length > 0 && (
    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
      {selectedItems.length}
    </Badge>
  )}
</div>


      {/* Trash zone — only visible while an item is being dragged */}
      <div
        ref={trashRef}
        onClick={onOpenTrash}
        className={`fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-2xl border shadow-lg backdrop-blur-md cursor-pointer transition-all duration-300 ${
          isDraggingItem
            ? 'border-destructive bg-destructive/20 text-destructive animate-pulse scale-110 opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none scale-90'
        }`}
      >
        <Trash2 className="h-6 w-6" />
      </div>

      {/* Drag overlay */}
        {isDraggingOver && (
        <div className="absolute inset-0 z-30 bg-primary/10 border-4 border-dashed border-primary rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-card/90 backdrop-blur-sm rounded-lg px-6 py-4 text-center">
            <p className="text-lg font-medium">Drop image here</p>
            <p className="text-sm text-muted-foreground">Image will be placed at drop location</p>
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[180px]"
          style={{ left: contextMenu.screenX, top: contextMenu.screenY }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => addNoteAtPosition(contextMenu.canvasX, contextMenu.canvasY)}
          >
            <FileText className="h-4 w-4" /> New note
          </button>
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => addTableAtPosition(contextMenu.canvasX, contextMenu.canvasY)}
          >
            <Table className="h-4 w-4" /> New table
          </button>
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => addNoteStickerAtPosition(contextMenu.canvasX, contextMenu.canvasY)}
          >
            <StickyNote className="h-4 w-4" /> Add note sticker
          </button>
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => addTodoAtPosition(contextMenu.canvasX, contextMenu.canvasY)}
          >
            <ListChecks className="h-4 w-4" /> Add to-do list
          </button>
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => pasteUrlAtPosition(contextMenu.canvasX, contextMenu.canvasY)}
          >
            <Link2 className="h-4 w-4" /> Paste URL
          </button>
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => pasteImageAtPosition(contextMenu.canvasX, contextMenu.canvasY)}
          >
            <ImagePlus className="h-4 w-4" /> Paste image
          </button>
          <div className="border-t border-border my-1" />
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
            onClick={() => { closeContextMenu(); onOpenTrash(); }}
          >
            <Trash2 className="h-4 w-4" /> Open trash
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className={`w-full h-full overflow-auto ${isSelecting ? 'select-none' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={handleContextMenu}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
      >
        {/*
          Sizing wrapper: `transform: scale()` on the board below is purely a paint-time
          effect and doesn't grow this container's scrollWidth/scrollHeight, so without this
          wrapper the scroll range stays pinned to the unscaled 6000x1000 box no matter how
          far in we zoom. That silently clamps the scroll compensation zoom does to keep the
          cursor's canvas point fixed, which is what made zooming always drift toward the
          top-left. Giving this wrapper the actual scaled footprint fixes the scroll range.
        */}
        <div style={{ width: 6000 * zoom, height: 1000 * zoom }}>
          <div
            ref={canvasRef}
            className={`relative bg-gradient-to-br from-background via-primary/5 to-accent/5 ${(selectMode || isSelecting) ? 'cursor-crosshair select-none' : ''}`}
            data-anotequest-canvas-board
            style={{
              width: `6000px`,
              height: `1000px`,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--border)) 1px, transparent 0)`,
              backgroundSize: '32px 32px',
              userSelect: (selectMode || isSelecting) ? 'none' : 'auto',
              WebkitUserSelect: (selectMode || isSelecting) ? 'none' : 'auto',
            }}
            onMouseDown={handleCanvasMouseDown}
          >
          {/* Selection Box Overlay */}
          {isSelecting && selectionBox && (
            <div
              className="absolute border-2 border-primary bg-primary/10 pointer-events-none z-50"
              style={{
                left: Math.min(selectionBox.startX, selectionBox.currentX),
                top: Math.min(selectionBox.startY, selectionBox.currentY),
                width: Math.abs(selectionBox.currentX - selectionBox.startX),
                height: Math.abs(selectionBox.currentY - selectionBox.startY),
              }}
            />
          )}

          {/* Drawing Canvas Layer */}
          <DrawingCanvas
            canvasSize={canvasSize}
            drawings={drawings}
            setDrawings={setDrawings}
            isActive={isDrawingMode}
            onClose={onCloseDrawing}
            zoom={zoom}
            scrollContainerRef={containerRef}
            drawingTool={drawingTool}
            activeFolder={activeFolder}
          />

          {/* Empty State */}
          {notes.length === 0 && stickers.length === 0 && images.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center space-y-4 animate-fadeIn">
                <div className="text-6xl">📝</div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground/80">Your canvas awaits!</h3>
                  <p className="text-muted-foreground">Click + to add notes, or drag images here</p>
                </div>
              </div>
            </div>
          )}

          {/* Render Notes */}
          {notes.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              updateNote={updateNote}
              deleteNote={deleteNote}
              addNote={addNote}
              folders={folders}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              onNoteClick={onNoteClick}
              onOpenNoteTools={onOpenNoteTools}
              openReaderMode={readerModeNoteId === note.id}
              onReaderModeClosed={onReaderModeClosed}
              isSelected={isItemSelected('note', note.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'note', note.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}

          {/* Render Stickers */}
          {stickers.map((sticker) => (
            <StickerItem
              key={sticker.id}
              sticker={sticker}
              updateSticker={updateSticker}
              deleteSticker={deleteSticker}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              isSelected={isItemSelected('sticker', sticker.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'sticker', sticker.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}

          {/* Render Images */}
          {images.map((image) => (
            <ImageItem
              key={image.id}
              image={image}
              updateImage={updateImage}
              deleteImage={deleteImage}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              isSelected={isItemSelected('image', image.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'image', image.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}

          {/* Render Tables */}
          {tables.map((table) => (
            <TableItem
              key={table.id}
              table={table}
              updateTable={updateTable}
              deleteTable={deleteTable}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              isSelected={isItemSelected('table', table.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'table', table.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}

          {/* Render Todos */}
          {todos.map((todo) => (
            <TodoItem
              key={todo.id}
              todo={todo}
              updateTodo={updateTodo}
              deleteTodo={deleteTodo}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              isSelected={isItemSelected('todo', todo.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'todo', todo.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}

          {/* Render Sources / Links */}
          {sources.map((source) => (
            <SourceItem
              key={source.id}
              source={source}
              updateSource={updateSource}
              deleteSource={deleteSource}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              isSelected={isItemSelected('source', source.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'source', source.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}

          {/* Render Note Stickers (highest layer) */}
          {noteStickers.map((sticker) => (
            <NoteSticker
              key={sticker.id}
              sticker={sticker}
              updateNoteSticker={updateNoteSticker}
              deleteNoteSticker={deleteNoteSticker}
              zoom={zoom}
              shouldDeleteOnDrop={shouldDeleteOnDrop}
              isSelected={isItemSelected('noteSticker', sticker.id)}
              onMultiDrag={(deltaX, deltaY) => handleMultiDrag(deltaX, deltaY, 'noteSticker', sticker.id)}
              selectedCount={selectedItems.length}
              onDragStart={() => setIsDraggingItem(true)}
              onDragEnd={() => setIsDraggingItem(false)}
            />
          ))}
        </div>
        </div>
      </div>
    </div>
  );
};

export default Canvas;
